import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { TabTitleService } from '../../layout/tab-title.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { IdentityUserSelectorComponent } from '../../shared/design-system/identity-user-selector.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CreateUserPartyBindingRequest, PlatformAdminService, UserPartyBindingRecord } from './platform-admin.service';
import {
  bindingStatusApiValue,
  bindingStatusLabelKey,
  bindingStatusSeverity,
  isActiveBindingStatus,
  isBuyerRelation,
  isSellerRelation,
  relationTypeApiValue,
  relationTypeLabelKey,
  USER_PARTY_BINDING_STATUSES,
  USER_PARTY_RELATION_TYPES,
  UserPartyBindingStatusName,
  UserPartyRelationTypeName
} from './platform-user-party-binding.utils';

@Component({
  standalone: true,
  selector: 'ain-platform-user-party-bindings',
  imports: [
    AinPageComponent,
    AinSearchAccordionComponent,
    AinStateComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    SelectModule,
    DialogModule,
    CheckboxModule,
    TagModule,
    IdentityUserSelectorComponent,
    PartySelectorComponent,
    CustomerSelectorComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('platform.eyebrow')"
      [title]="t('platform.bindings.title')"
      [description]="t('platform.bindings.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('platform.bindings.create')" (click)="openCreateDialog()"></button>
            <button pButton type="button" severity="secondary" [label]="t('common.edit')" [disabled]="selectedRows().length !== 1" (click)="openSelectedDetail()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
          </div>

          <ain-search-accordion [title]="t('platform.search.title')">
            <section class="search-grid">
              <label>
                <span>{{ t('platform.bindings.fields.user') }}</span>
                <ain-identity-user-selector [(ngModel)]="filters.userId" name="filterUserId" />
              </label>
              <label>
                <span>{{ t('platform.bindings.fields.party') }}</span>
                <ain-party-selector [(ngModel)]="filters.partyId" name="filterPartyId" />
              </label>
              <label>
                <span>{{ t('platform.bindings.fields.customerAccount') }}</span>
                <ain-customer-selector [(ngModel)]="filters.customerAccountId" name="filterCustomerAccountId" />
              </label>
              <label>
                <span>{{ t('platform.bindings.filters.relationType') }}</span>
                <p-select
                  [(ngModel)]="filters.relationType"
                  [options]="relationTypeOptions"
                  optionValue="value"
                  [showClear]="true"
                  [placeholder]="t('common.all')"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? relationTypeLabel(item.value) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ relationTypeLabel(item.value) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('platform.bindings.filters.status') }}</span>
                <p-select
                  [(ngModel)]="filters.status"
                  [options]="statusOptions"
                  optionValue="value"
                  [showClear]="true"
                  [placeholder]="t('common.all')"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? statusLabel(item.value) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ statusLabel(item.value) }}</ng-template>
                </p-select>
              </label>
              <div class="search-actions">
                <button pButton type="button" [label]="t('platform.search.apply')" (click)="applyFilters()"></button>
                <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
              </div>
            </section>
          </ain-search-accordion>

          @if (repairRows().length) {
            <section class="repair-panel">
              <h3>{{ t('platform.bindings.repair.title') }}</h3>
              <p>{{ t('platform.bindings.repair.description') }}</p>
              <p-table [value]="repairRows()" [paginator]="true" [rows]="5">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('platform.bindings.fields.user') }}</th>
                    <th>{{ t('platform.bindings.repair.missingBuyer') }}</th>
                    <th>{{ t('platform.bindings.repair.missingSeller') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td class="selector-cell">
                      <ain-identity-user-selector [ngModel]="row.userId" [disabled]="true" />
                    </td>
                    <td>{{ row.missingBuyer ? t('common.yes') : t('common.no') }}</td>
                    <td>{{ row.missingSeller ? t('common.yes') : t('common.no') }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          }

          @if (!rows().length) {
            <ain-state type="empty" [title]="t('common.noRecords')" />
          } @else {
            <p-table
              [value]="rows()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('platform.bindings.fields.user') }}</th>
                  <th>{{ t('platform.bindings.fields.party') }}</th>
                  <th>{{ t('platform.bindings.fields.customerAccount') }}</th>
                  <th>{{ t('platform.bindings.fields.relationType') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th>{{ t('platform.bindings.fields.isPrimary') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td class="selector-cell">
                    <button type="button" class="link-btn" (click)="openDetail(row.id)">
                      <ain-identity-user-selector [ngModel]="row.userId" [disabled]="true" />
                    </button>
                  </td>
                  <td class="selector-cell"><ain-party-selector [ngModel]="row.partyId" [disabled]="true" /></td>
                  <td class="selector-cell">
                    @if (row.customerAccountId) {
                      <ain-customer-selector [ngModel]="row.customerAccountId" [disabled]="true" />
                    } @else {
                      —
                    }
                  </td>
                  <td>{{ relationTypeLabel(row.relationType) }}</td>
                  <td>
                    <p-tag [severity]="statusSeverity(row.status)" [value]="statusLabel(row.status)" />
                  </td>
                  <td>{{ row.isPrimary ? t('common.yes') : t('common.no') }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }

      <p-dialog
        [header]="t('platform.bindings.create')"
        [visible]="createDialogVisible()"
        [modal]="true"
        [style]="{ width: '48rem', maxWidth: '95vw' }"
        (visibleChange)="createDialogVisible.set($event)">
        <section class="create-grid">
          <label>
            <span>{{ t('platform.bindings.fields.user') }}</span>
            <ain-identity-user-selector [(ngModel)]="createModel.userId" name="createUserId" />
          </label>
          <label>
            <span>{{ t('platform.bindings.fields.party') }}</span>
            <ain-party-selector [(ngModel)]="createModel.partyId" name="createPartyId" />
          </label>
          <label>
            <span>{{ t('platform.bindings.fields.customerAccount') }}</span>
            <ain-customer-selector [(ngModel)]="createModel.customerAccountId" name="createCustomerAccountId" />
          </label>
          <label>
            <span>{{ t('platform.bindings.fields.relationType') }}</span>
            <p-select
              [(ngModel)]="createModel.relationType"
              [options]="relationTypeOptions"
              optionValue="value"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? relationTypeLabel(item.value) : '-' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ relationTypeLabel(item.value) }}</ng-template>
            </p-select>
          </label>
          <label>
            <span>{{ t('columns.status') }}</span>
            <p-select
              [(ngModel)]="createModel.status"
              [options]="statusOptions"
              optionValue="value"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? statusLabel(item.value) : '-' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ statusLabel(item.value) }}</ng-template>
            </p-select>
          </label>
          <label class="checkbox-row">
            <p-checkbox [(ngModel)]="createModel.isPrimary" [binary]="true" inputId="bindingIsPrimary" />
            <span>{{ t('platform.bindings.fields.isPrimary') }}</span>
          </label>
        </section>
        <div class="dialog-actions">
          <button pButton type="button" [label]="t('common.save')" (click)="createBinding()" [disabled]="saving()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible.set(false)"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .search-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; align-items: end; }
    .search-grid label { display: grid; gap: 6px; }
    .search-grid span { font-size: 12px; font-weight: 600; color: #475569; }
    .search-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .create-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
    .create-grid label { display: grid; gap: 6px; }
    .create-grid span { font-size: 12px; font-weight: 600; color: #475569; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .dialog-actions { display: flex; gap: 8px; margin-top: 16px; flex-wrap: wrap; }
    .repair-panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #f8fafc; }
    .repair-panel h3 { margin: 0 0 6px; }
    .repair-panel p { margin: 0 0 10px; color: #64748b; font-size: 13px; }
    .selector-cell { min-width: 180px; }
    .link-btn { border: 0; background: none; padding: 0; cursor: pointer; width: 100%; text-align: left; }
    @media (max-width: 1200px) { .search-grid, .create-grid { grid-template-columns: 1fr; } }
  `]
})
export class PlatformUserPartyBindingsComponent implements OnInit {
  private readonly api = inject(PlatformAdminService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly tabTitleService = inject(TabTitleService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly rows = signal<UserPartyBindingRecord[]>([]);
  readonly repairRows = signal<Array<{ userId: string; missingBuyer: boolean; missingSeller: boolean }>>([]);
  readonly selectedRows = signal<UserPartyBindingRecord[]>([]);
  readonly createDialogVisible = signal(false);

  readonly relationTypeOptions = USER_PARTY_RELATION_TYPES.map(value => ({ value }));
  readonly statusOptions = USER_PARTY_BINDING_STATUSES.map(value => ({ value }));

  filters = {
    userId: null as string | null,
    partyId: null as string | null,
    customerAccountId: null as string | null,
    relationType: null as UserPartyRelationTypeName | null,
    status: null as UserPartyBindingStatusName | null
  };

  createModel = {
    userId: null as string | null,
    partyId: null as string | null,
    customerAccountId: null as string | null,
    relationType: 'Buyer' as UserPartyRelationTypeName,
    status: 'Active' as UserPartyBindingStatusName,
    isPrimary: true
  };

  ngOnInit(): void {
    this.tabTitleService.setCurrent(this.t('platform.bindings.title'));
    this.route.queryParamMap.subscribe(params => {
      this.filters.userId = params.get('userId');
      this.filters.partyId = params.get('partyId');
      this.filters.customerAccountId = params.get('customerAccountId');
      const relationType = params.get('relationType');
      this.filters.relationType = relationType
        ? (USER_PARTY_RELATION_TYPES.find(item => item === relationType) ?? null)
        : null;
      const status = params.get('status');
      this.filters.status = status
        ? (USER_PARTY_BINDING_STATUSES.find(item => item === status) ?? null)
        : null;
      this.load();
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  relationTypeLabel(value: number | string): string {
    const key = relationTypeLabelKey(value);
    return key ? this.t(key) : `${value}`;
  }

  statusLabel(value: number | string): string {
    const key = bindingStatusLabelKey(value);
    return key ? this.t(key) : `${value}`;
  }

  statusSeverity(value: number | string): 'success' | 'warn' | 'danger' | 'secondary' {
    return bindingStatusSeverity(value);
  }

  onSelectionChange(rows: UserPartyBindingRecord[]): void {
    this.selectedRows.set(rows);
  }

  applyFilters(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.filterQueryParams(),
      queryParamsHandling: 'merge'
    });
  }

  load(): void {
    this.loading.set(true);
    this.api.listUserPartyBindings({
      userId: this.filters.userId || undefined,
      partyId: this.filters.partyId || undefined,
      customerAccountId: this.filters.customerAccountId || undefined,
      relationType: this.filters.relationType ? relationTypeApiValue(this.filters.relationType) : undefined,
      status: this.filters.status ? bindingStatusApiValue(this.filters.status) : undefined
    }).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.repairRows.set(this.buildRepairRows(rows));
        this.selectedRows.set([]);
        this.loading.set(false);
      },
      error: () => {
        this.messages.add({ severity: 'error', summary: this.t('platform.bindings.errorLoad') });
        this.loading.set(false);
      }
    });
  }

  clearFilters(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        userId: null,
        partyId: null,
        customerAccountId: null,
        relationType: null,
        status: null
      },
      queryParamsHandling: 'merge'
    });
  }

  openCreateDialog(): void {
    this.createModel = {
      userId: null,
      partyId: null,
      customerAccountId: null,
      relationType: 'Buyer',
      status: 'Active',
      isPrimary: true
    };
    this.createDialogVisible.set(true);
  }

  createBinding(): void {
    const userId = this.createModel.userId?.trim();
    const partyId = this.createModel.partyId?.trim();
    if (!userId || !partyId) {
      this.messages.add({ severity: 'warn', summary: this.t('platform.bindings.validation.userPartyRequired') });
      return;
    }

    const request: CreateUserPartyBindingRequest = {
      partyId,
      customerAccountId: this.createModel.customerAccountId?.trim() || null,
      relationType: relationTypeApiValue(this.createModel.relationType),
      status: bindingStatusApiValue(this.createModel.status),
      isPrimary: this.createModel.isPrimary
    };

    this.saving.set(true);
    this.api.createUserPartyBinding(userId, request).subscribe({
      next: () => {
        this.createDialogVisible.set(false);
        this.saving.set(false);
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.messages.add({ severity: 'error', summary: this.t('platform.bindings.errorCreate') });
      }
    });
  }

  openSelectedDetail(): void {
    const selected = this.selectedRows()[0];
    if (selected) {
      this.openDetail(selected.id);
    }
  }

  openDetail(bindingId: string): void {
    void this.router.navigate(['/platform/user-party-bindings', bindingId], {
      queryParams: this.filterQueryParams()
    });
  }

  private filterQueryParams(): Record<string, string | null> {
    return {
      userId: this.filters.userId ?? null,
      partyId: this.filters.partyId ?? null,
      customerAccountId: this.filters.customerAccountId ?? null,
      relationType: this.filters.relationType ?? null,
      status: this.filters.status ?? null
    };
  }

  private buildRepairRows(rows: UserPartyBindingRecord[]): Array<{ userId: string; missingBuyer: boolean; missingSeller: boolean }> {
    const users = new Set(rows.map(item => item.userId));
    const repair: Array<{ userId: string; missingBuyer: boolean; missingSeller: boolean }> = [];
    for (const userId of users) {
      const userBindings = rows.filter(item => item.userId === userId && isActiveBindingStatus(item.status));
      const hasBuyer = userBindings.some(item => isBuyerRelation(item.relationType));
      const hasSeller = userBindings.some(item => isSellerRelation(item.relationType));
      if (!hasBuyer || !hasSeller) {
        repair.push({ userId, missingBuyer: !hasBuyer, missingSeller: !hasSeller });
      }
    }
    return repair;
  }
}
